import {
  Box,
  CircularProgress,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { getPolyglot } from '../../../../i18n';
import { WorkspaceAPI } from '../../../../redux/stoerkID/StoerkIdAPI.model';
import useWatchError from '../../../../handlingErrors/useWatchError';
import StoerkIdAPI from '../../../../redux/stoerkID/StoerkIdAPI';
import { useAppDispatch, useAppSelector } from '../../../../redux/store.model';
import AdditionalContactInfo from './AdditionalContactInfo';
import LabelTitle from '../../../../theme/components/Forms/LabelTitle';

export interface StoerkIdDetailsProps {
  workspace?: WorkspaceAPI;
  showAccountData?: boolean;
}
export function StoerkIdStatus(props: StoerkIdDetailsProps) {
  const { workspace, showAccountData } = props;

  const polyglot = getPolyglot();
  const theme = useTheme();
  return (
    <Box
      sx={{
        '& .StcTableCell-head': {
          fontWeight: 'bold',
        },
        '& table': {
          width: '100%',
          textAlign: 'left',
          '& .StcTableRow-head': {
            ...theme.typography.h3,
            fontSize: '0.9rem',
          },
          '& .StcTableBody-root:before': {
            lineHeight: '0.5em',
            content: '"-"',
            display: 'block',
            color: 'transparent',
          },
          '& .StcTableCell-body': {
            ...theme.typography.body1,
            fontSize: '0.8rem',
          },
        },
      }}
    >
      <Typography variant="h2" sx={{ marginBottom: 2 }}>
        {polyglot.t('stoerk_id.detail_page.status.subtitle')}
      </Typography>

      {workspace ? (
        <TableActive workspace={workspace} showAccountData={showAccountData} />
      ) : (
        <TableInactive />
      )}
    </Box>
  );
}

export interface TableActivesProps {
  workspace: WorkspaceAPI;
  billingInfo?: any;
  showAccountData?: boolean;
}

const TableActive = (props: TableActivesProps) => {
  const { workspace, showAccountData } = props;
  const polyglot = getPolyglot();
  const dispatch = useAppDispatch();

  const {
    data: billingInfo,
    isLoading: isLoadingBillingInfo,
    error: errorBillingInfo,
  } = StoerkIdAPI.useGetBillingInfoFromWorkspaceQuery(workspace?.id);

  const {
    data: workspaceUsage,
    isLoading: isLoadingWorkspaceUsage,
    error: errorWorkspaceUsage,
  } = StoerkIdAPI.useGetWorkspaceUsageQuery(workspace?.id);

  // Fetch subworkspaces to determine if supplier
  const { data: subWorkspaces } = StoerkIdAPI.useGetSubWorkspacesQuery(
    workspace?.id
  );

  const childIds = useMemo(
    () => (subWorkspaces ? Object.keys(subWorkspaces) : []),
    [subWorkspaces]
  );

  // Fetch usage for each child workspace
  useEffect(() => {
    childIds.forEach((childId) => {
      dispatch(StoerkIdAPI.endpoints.getWorkspaceUsage.initiate(childId));
    });
  }, [dispatch, childIds]);

  // Sum up child usage
  const childUsageTotals = useAppSelector((state) => {
    if (childIds.length === 0) return { commanders: 0, users: 0 };
    let commanders = 0;
    let users = 0;
    childIds.forEach((childId) => {
      const usage =
        StoerkIdAPI.endpoints.getWorkspaceUsage.select(childId)(state).data;
      commanders += usage?.commanders?.length ?? 0;
      users += usage?.users?.length ?? 0;
    });
    return { commanders, users };
  });

  const isSupplier = childIds.length > 0;
  const totalCommanders =
    (workspaceUsage?.commanders?.length ?? 0) +
    (isSupplier ? childUsageTotals.commanders : 0);
  const totalUsers =
    (workspaceUsage?.users?.length ?? 0) +
    (isSupplier ? childUsageTotals.users : 0);

  // Total quota = supplier's own quota + sum of child allocated quotas
  const totalDeviceQuota = useMemo(() => {
    if (!isSupplier || !subWorkspaces) return workspace?.quota.devices ?? 0;
    const childAllocated = Object.values(subWorkspaces).reduce(
      (s, a) => s + a.devices,
      0
    );
    return (workspace?.quota.devices ?? 0) + childAllocated;
  }, [isSupplier, subWorkspaces, workspace?.quota.devices]);

  const totalUserQuota = useMemo(() => {
    if (!isSupplier || !subWorkspaces) return workspace?.quota.users ?? 0;
    const childAllocated = Object.values(subWorkspaces).reduce(
      (s, a) => s + a.users,
      0
    );
    return (workspace?.quota.users ?? 0) + childAllocated;
  }, [isSupplier, subWorkspaces, workspace?.quota.users]);

  const error = errorBillingInfo || errorWorkspaceUsage;
  useWatchError(error);

  return (
    <Grid container spacing={1} direction={{ xs: 'column', md: 'row' }}>
      <Grid item xs={4} md={4}>
        <LabelTitle>
          {polyglot.t('stoerk_id.detail_page.status.status_header_cell')}
        </LabelTitle>
        <Typography variant="body2">
          {polyglot.t('stoerk_id.detail_page.status.status_active')}
        </Typography>
      </Grid>
      <Grid item xs={4} md={4}>
        <LabelTitle>
          {polyglot.t('stoerk_id.detail_page.status.company_name_header_cell')}
        </LabelTitle>
        <Typography variant="body2">{workspace.name || '-'}</Typography>
      </Grid>

      <Grid item xs={4} md={2}>
        <LabelTitle>
          {polyglot.t('stoerk_id.detail_page.status.commanders_header_cell')}
        </LabelTitle>
        <Typography variant="body2">
          {isLoadingWorkspaceUsage ? (
            <Skeleton width={60} />
          ) : (
            `${totalCommanders} / ${totalDeviceQuota}`
          )}
        </Typography>
      </Grid>
      <Grid item xs={4} md={2}>
        <LabelTitle>
          {polyglot.t('stoerk_id.detail_page.status.users_header_cell')}
        </LabelTitle>
        <Typography variant="body2">
          {isLoadingWorkspaceUsage ? (
            <Skeleton width={60} />
          ) : (
            `${totalUsers} / ${totalUserQuota}`
          )}
        </Typography>
      </Grid>

      {isSupplier && (
        <>
          <Grid item xs={4} md={2}>
            <LabelTitle>
              {polyglot.t(
                'stoerk_id.detail_page.status.supplier_commanders_header_cell'
              )}
            </LabelTitle>
            <Typography variant="body2">
              {isLoadingWorkspaceUsage ? (
                <Skeleton width={60} />
              ) : (
                `${workspaceUsage?.commanders?.length ?? 0} / ${
                  workspace?.quota.devices
                }`
              )}
            </Typography>
          </Grid>
          <Grid item xs={4} md={2}>
            <LabelTitle>
              {polyglot.t(
                'stoerk_id.detail_page.status.supplier_users_header_cell'
              )}
            </LabelTitle>
            <Typography variant="body2">
              {isLoadingWorkspaceUsage ? (
                <Skeleton width={60} />
              ) : (
                `${workspaceUsage?.users?.length ?? 0} / ${
                  workspace?.quota.users
                }`
              )}
            </Typography>
          </Grid>
        </>
      )}

      {(billingInfo?.pricingDetails?.text || isLoadingBillingInfo) && (
        <Grid item xs={12}>
          <LabelTitle>
            {polyglot.t('stoerk_id.detail_page.status.price_header_cell')}
          </LabelTitle>

          {isLoadingBillingInfo ? (
            <CircularProgress size={'1em'} />
          ) : billingInfo?.pricingDetails?.text ? (
            <Typography variant="body2">
              {billingInfo?.pricingDetails?.text ?? '--'}
            </Typography>
          ) : null}
        </Grid>
      )}

      {showAccountData && <AdditionalContactInfo workspace={workspace} />}

      {/*
      <Grid item xs={4} md={3}>
        <LabelTitle>
          {polyglot.t('stoerk_id.detail_page.status.invoice_header_cell')}
        </LabelTitle>
        <Typography variant="body2">06/02/2023 (Mock)</Typography>
      </Grid> */}
    </Grid>
  );
};

const TableInactive = () => {
  const polyglot = getPolyglot();

  return (
    <Grid container spacing={1}>
      <Grid item xs={4} md={4}>
        <LabelTitle>
          {polyglot.t('stoerk_id.detail_page.status.status_header_cell')}
        </LabelTitle>
        <Typography variant="body2">
          {polyglot.t('stoerk_id.detail_page.status.status_inactive')}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default StoerkIdStatus;
